/**
 * Returns a formatted string showing elapsed time since `startTime`.
 *
 * Supports:
 *   getElapsedTime(start)
 *   getElapsedTime(start, end)
 *   getElapsedTime(start, { isParked, parkedAt, totalPausedSeconds, endTime })
 *
 * - While parked: uses `parkedAt` as end time (freezes timer)
 * - totalPausedSeconds is subtracted from the diff so timers resume correctly.
 */
export function getElapsedTime(startTime, arg2) {
  if (!startTime) return "0:00";

  const start = new Date(startTime);
  if (isNaN(start.getTime())) return "0:00";

  let end;
  let isParked = false;
  let parkedAt = null;
  let totalPausedSeconds = 0;

  // Case 1: options object (park info)
  if (arg2 && typeof arg2 === "object" && !(arg2 instanceof Date)) {
    isParked = !!arg2.isParked;
    parkedAt = arg2.parkedAt ? new Date(arg2.parkedAt) : null;
    totalPausedSeconds = Number.isFinite(arg2.totalPausedSeconds)
      ? arg2.totalPausedSeconds
      : 0;

    if (arg2.endTime) {
      end = new Date(arg2.endTime);
    }
  }
  // Case 2: second arg is an explicit end time (Date/string)
  else if (arg2) {
    end = new Date(arg2);
  }

  // Decide end time
  if (!end) {
    if (isParked && parkedAt && !isNaN(parkedAt.getTime())) {
      // parked: freeze at parkedAt
      end = parkedAt;
    } else {
      // live: keep ticking
      end = new Date();
    }
  }

  if (isNaN(end.getTime())) return "0:00";

  let diff = Math.floor((end - start) / 1000) - totalPausedSeconds;
  if (diff < 0) diff = 0;

  const mins = Math.floor(diff / 60);
  const secs = diff % 60;

  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Returns how long a ticket item was active.
 * - If bumped: from createdAt → bumpedAt
 * - Else if parked: from createdAt → parkedAt (frozen)
 * - Else: from createdAt → now
 * Subtracts totalPausedSeconds so timers resume correctly after unpark.
 * Returns "--:--" on error or invalid input.
 */
export function getItemElapsedTime(item) {
  try {
    if (!item || !item.createdAt) return "--:--";

    const start = new Date(item.createdAt);
    if (isNaN(start.getTime())) return "--:--";

    // total time previously paused (from earlier park sessions)
    const pausedTotal = Number.isFinite(item.totalPausedSeconds)
      ? item.totalPausedSeconds
      : 0;

    let end;

    if (item.isBumped && item.bumpedAt) {
      // Done cooking: freeze at bumped time
      end = new Date(item.bumpedAt);
    } else if (item.isParked && item.parkedAt) {
      // Parked: freeze at parkedAt, do NOT keep counting
      end = new Date(item.parkedAt);
    } else {
      // Live item: use current time
      end = new Date();
    }

    if (isNaN(end.getTime())) return "--:--";

    let diff = Math.floor((end - start) / 1000) - pausedTotal;
    if (diff < 0) diff = 0;

    const mins = Math.floor(diff / 60);
    const secs = diff % 60;

    return `${mins}:${secs.toString().padStart(2, "0")}`;
  } catch (err) {
    console.error("getItemElapsedTime error:", err);
    return "--:--";
  }
}

/**
 * Returns the current time as a human-readable string.
 * Example: "03:15:42 PM"
 */
export function getCurrentTime() {
  const now = new Date();
  return now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * If a value looks like minutes (e.g. 5, 10, 15) treat as minutes.
 * If it looks like seconds (e.g. 300, 600) treat as seconds.
 */
function normalizeThresholdSeconds(value, fallbackSeconds) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallbackSeconds;

  if (n < 60) return Math.round(n * 60);
  return Math.round(n);
}

function getColorClassFromElapsed(elapsedSec, thresholds) {
  const yellowAfterSec = thresholds?.yellowAfterSec ?? 300;
  const redAfterSec = thresholds?.redAfterSec ?? 600;

  // Ensure ordering if user fat-fingers values
  const yellow = Math.min(yellowAfterSec, redAfterSec);
  const red = Math.max(yellowAfterSec, redAfterSec);

  if (elapsedSec >= red) {
    return "bg-red-500/20 dark:bg-red-500/20 text-red-800 dark:text-red-200";
  }
  if (elapsedSec >= yellow) {
    return "bg-yellow-500/20 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-200";
  }
  return "bg-green-500/20 dark:bg-green-500/20 text-green-800 dark:text-green-200";
}

function getTicketThresholds(settings = {}) {
  return {
    yellowAfterSec: normalizeThresholdSeconds(
      settings?.ticket_color_yellow_after,
      300
    ),
    redAfterSec: normalizeThresholdSeconds(settings?.ticket_color_red_after, 600),
  };
}

function getCourseThresholds(settings = {}) {
  return {
    yellowAfterSec: normalizeThresholdSeconds(
      settings?.course_color_yellow_after,
      420 // 7 minutes default
    ),
    redAfterSec: normalizeThresholdSeconds(
      settings?.course_color_red_after,
      720 // 12 minutes default
    ),
  };
}

/**
 * Returns a Tailwind color class based on ticket or course timing.
 * If `ticket_color_by_course` is true and `coursing` is enabled,
 * the color is based on the active (fired but not yet bumped) course time.
 */
export function getCardColor(createdAt, items = [], settings = {}) {
  const created = new Date(createdAt);
  const createdMs = isNaN(created.getTime()) ? Date.now() : created.getTime();

  if (settings.ticket_color_by_course && settings.coursing) {
    const courseThresholds = getCourseThresholds(settings);

    // Get the lowest unbumped course number (active course)
    const unbumpedCourses = [
      ...new Set(
        items
          .filter(
            (i) =>
              !i.isCourseSeparator &&
              !i.isBumped &&
              typeof i.course === "number"
          )
          .map((i) => i.course)
      ),
    ].sort((a, b) => a - b);

    const activeCourse = unbumpedCourses[0];

    if (typeof activeCourse !== "number") {
      // All items are bumped; ticket is done
      return getColorClassFromElapsed(0, courseThresholds);
    }

    // For course 0, use ticket created time
    if (activeCourse === 0) {
      const elapsed = Math.floor((Date.now() - createdMs) / 1000);
      return getColorClassFromElapsed(elapsed, courseThresholds);
    }

    // Otherwise, find when the previous course (activeCourse - 1) was fully bumped
    const previousCourseItems = items.filter(
      (i) => i.course === activeCourse - 1 && !i.isCourseSeparator
    );

    const allBumped =
      previousCourseItems.length > 0 &&
      previousCourseItems.every((i) => i.isBumped);

    if (!allBumped) {
      // Course isn't really active until previous is fully bumped
      return getColorClassFromElapsed(0, courseThresholds);
    }

    const bumpTimes = previousCourseItems
      .map((i) => i.bumpedAt)
      .filter(Boolean)
      .map((t) => new Date(t))
      .filter((d) => !isNaN(d.getTime()));

    const latestBump = bumpTimes.sort((a, b) => b - a)[0];

    const baseTime = latestBump || new Date(createdMs);
    const elapsed = Math.floor((Date.now() - baseTime.getTime()) / 1000);
    return getColorClassFromElapsed(elapsed, courseThresholds);
  }

  // fallback: use ticket createdAt + ticket thresholds
  const ticketThresholds = getTicketThresholds(settings);
  const fallbackElapsed = Math.floor((Date.now() - createdMs) / 1000);
  return getColorClassFromElapsed(fallbackElapsed, ticketThresholds);
}

/**
 * Gets timing info for all courses on a ticket.
 * Returns: [{ course, firstFiredAt, firedFor, lastBumpedAt, bumpedFor }]
 */
export function getCourseTimingData(items) {
  const grouped = new Map(); // course => [items]

  for (const item of items) {
    if (item.course == null) continue;
    if (!grouped.has(item.course)) grouped.set(item.course, []);
    grouped.get(item.course).push(item);
  }

  const results = [];

  for (const [course, courseItems] of grouped.entries()) {
    // Only consider real items, not separators or messages
    const realItems = courseItems.filter(
      (i) => !i.isCourseSeparator && !i.isMessage && !i.isFireItem
    );

    const firedItems = realItems.filter((i) => i.isFired && i.firedAt);
    const firstFired =
      firedItems.length > 0
        ? new Date(
          Math.min(...firedItems.map((i) => new Date(i.firedAt).getTime()))
        )
        : null;

    const allBumped =
      realItems.length > 0 && realItems.every((i) => i.isBumped && i.bumpedAt);

    const lastBumped = allBumped
      ? new Date(
        Math.max(...realItems.map((i) => new Date(i.bumpedAt).getTime()))
      )
      : null;

    if (allBumped && firedItems.length > 0 && firstFired && lastBumped) {
      const frozen = getElapsedTime(firstFired, lastBumped);
      realItems.forEach((i) => {
        if (!i.frozenFiredFor) i.frozenFiredFor = frozen;
      });
    }

    results.push({
      course,
      firstFiredAt: firstFired?.toLocaleTimeString() ?? "--",
      firedFor: firstFired
        ? realItems.find((i) => i.frozenFiredFor)?.frozenFiredFor ??
        getElapsedTime(firstFired)
        : "--:--",
      lastBumpedAt: lastBumped?.toLocaleTimeString() ?? "--",
      bumpedFor: lastBumped ? getElapsedTime(lastBumped) : "--:--",
      firstFiredDate: firstFired || null,
    });
  }

  return results.sort((a, b) => a.course - b.course);
}

/**
 * Returns the earliest fired time from a list of items.
 * If no items are fired, returns null.
 */
export function getTicketFiredTime(items) {
  const firedItems = items.filter((i) => i.isFired && i.firedAt);
  if (firedItems.length === 0) return null;

  return new Date(
    Math.min(...firedItems.map((i) => new Date(i.firedAt).getTime()))
  );
}
